import React, { useMemo, useCallback } from "react";
import { Form, Checkbox, Input, Table, message, Row, Col, Radio } from "antd";

const Section6 = ({ formValues, setFormValues, handleNext }) => {
  const industries = [
    "Healthcare",
    "Real Estate & Housing",
    "Retail & ECommerce",
    "Food & Beverage",
    "Nonprofits & Community Organizations",
    "Financial Services",
    "Education",
    "Legal Services",
    "Automotive",
    "Tourism & Hospitality",
    "Tech & SaaS",
    "Government & Public Sector",
    "Other (please specify)",
  ];

  // Q25 limit
  const handleTopIndustriesChange = useCallback((checked) => {
    if (checked.length > 5) {
      message.warning("You can select up to 5 top specialties only.");
      return;
    }
    setFormValues({ ...formValues, topIndustries: checked });
  }, [formValues, setFormValues]);

  // Q24 change: keep everything in sync
  const handleIndustriesChange = useCallback((nextIndustries) => {
    const prevTop = formValues.topIndustries || [];
    const nextTop = prevTop.filter((i) => nextIndustries.includes(i));

    // remove PoW cells for industries that were removed from Q24
    const removedIndustries = prevTop.filter((i) => !nextIndustries.includes(i));
    const nextPoW = { ...(formValues.proofOfWork || {}) };
    if (removedIndustries.length) {
      Object.keys(nextPoW).forEach((svc) => {
        removedIndustries.forEach((ind) => {
          if (nextPoW[svc]) {
            delete nextPoW[svc][ind];
            if (Object.keys(nextPoW[svc]).length === 0) delete nextPoW[svc];
          }
        });
      });
    }

    // clear "Other" text if unchecked
    const nextOther = nextIndustries.includes("Other (please specify)")
      ? (formValues.otherIndustries || "")
      : "";

    setFormValues({
      ...formValues,
      industries: nextIndustries,
      topIndustries: nextTop,
      proofOfWork: nextPoW,
      otherIndustries: nextOther,
    });
  }, [formValues, setFormValues]);

  const selectedIndustries = formValues.topIndustries || [];
  const selectedServices = formValues.coreSpecialties || [];
  const rangeOptions = ["0", "1-2", "3-5", "6+"];

  // build columns with stable keys; recompute when deps change
  const columns = useMemo(() => ([
    { title: "Service", dataIndex: "service", key: "service", fixed: "left" },
    ...selectedIndustries.map((industry) => ({
      title: industry,
      dataIndex: industry,
      key: `ind-${industry}`,
      render: (_, record) => (
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          size="small"
          value={(formValues.proofOfWork?.[record.service] || {})[industry] || ""}
          onChange={(e) => {
            const value = e.target.value;
            const updatedProof = {
              ...(formValues.proofOfWork || {}),
              [record.service]: {
                ...(formValues.proofOfWork?.[record.service] || {}),
                [industry]: value,
              },
            };
            setFormValues({ ...formValues, proofOfWork: updatedProof });
          }}
        >
          {rangeOptions.map((opt) => (
            <Radio.Button key={opt} value={opt}>{opt}</Radio.Button>
          ))}
        </Radio.Group>
      ),
    })),
  ]), [selectedIndustries, formValues.proofOfWork, setFormValues, rangeOptions]);

  const data = useMemo(
    () => (selectedServices || []).map((service) => ({ key: service, service })),
    [selectedServices]
  );

  const renderCheckboxGrid = (options, selectedValues, keyName, onChangeHandler) => (
    <Checkbox.Group
      value={selectedValues || []}
      onChange={onChangeHandler || ((checked) => setFormValues({ ...formValues, [keyName]: checked }))}
      style={{ width: "100%" }}
    >
      <Row gutter={[0, 8]}>
        {options.map((option) => (
          <Col span={12} key={option}>
            <Checkbox value={option}>{option}</Checkbox>
          </Col>
        ))}
      </Row>
    </Checkbox.Group>
  );

  return (
    <Form layout="vertical">
      <Form.Item label="24. In which industry sectors have you delivered marketing or advertising services?">
        {/* use synced handler */}
        {renderCheckboxGrid(industries, formValues.industries, "industries", handleIndustriesChange)}
      </Form.Item>

      {formValues.industries?.includes("Other (please specify)") && (
        <Form.Item label="Please specify other industries">
          <Input
            value={formValues.otherIndustries || ""}
            onChange={(e) =>
              setFormValues({ ...formValues, otherIndustries: e.target.value })
            }
          />
        </Form.Item>
      )}

      <Form.Item label="25. Of the industries you selected above, which 5 represent your top areas of specialization? (Select up to 5)">
        {renderCheckboxGrid(
          formValues.industries || [],
          formValues.topIndustries,
          "topIndustries",
          handleTopIndustriesChange
        )}
      </Form.Item>

      {selectedIndustries.length > 0 && selectedServices.length > 0 && (
        <>
          <h3>26. Proof of Work – Campaigns executed in past 12 months</h3>
          <p>
            Enter how many campaigns you’ve executed for each selected{" "}
            <strong>service × industry</strong> in the past year.
            <br />
            <strong>Note:</strong> Any “0” where you claimed both that service
            and that industry will be flagged for review.
          </p>
          <Table
            key={`cols-${selectedIndustries.join("|")}`}
            columns={columns}
            dataSource={data}
            pagination={false}
            scroll={{ x: true }}
            bordered
          />
        </>
      )}
    </Form>
  );
};

export default Section6;
